import React from 'react';
import styled from '@emotion/styled';
import { Box, Flex, xcss, Text } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { AtlassianLogo } from '@atlaskit/logo-latest';
import Button from '@atlaskit/button';
import { useOpenClose } from '@css-xp/ppc-custom-hooks';
import { getPartnerServiceDeskUrlForGenericHelp } from '@css-xp/ppc-navigation';
import OrderFailureWarningIcon from './assets/order-failure-warning.svg';
import PartialOrderSuccessIcon from './assets/partial-order-success.svg';
import { FailureItemModal } from '../failure-modal/failure-item-modal';
import { ADMIN_PERMISSION_URL } from '../utils';
import { FailureVariants } from '../order-confirmation-container';
import '../../order-confirmation.scss';
import { StyledFooter } from '../order-confirmation.styles';

const StyledFailureScreenWrapper = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  paddingTop: '10%',
});

const StyledContentWrapper = styled.div<{ isDesignIteration1Enabled?: boolean }>`
  max-width: ${(props) => (props.isDesignIteration1Enabled ? '100%' : '440px')};
`;

const StyledContentRow = styled.div({
  marginBottom: '15px',
});

const StyledContentCol = styled.div({
  display: 'flex',
  flexDirection: 'column',
});

const StyledHeadingWrapper = styled(StyledContentRow)({
  marginBottom: '20px',
});

const StyledWarningImg = styled.img`
  margin-bottom: 20px;
  display: flex;
  justify-self: center;
`;

const StyledImgContainer = styled.img<{ shouldCenterAlignImg?: boolean }>`
  margin-bottom: 20px;
  max-width: 50%;
  position: relative;
  left: ${(props) => (props.shouldCenterAlignImg ? '35%' : '0')};
  height: 96px;
  width: 96px;
`;

const StyledSuccessContent = styled.div<{ isDesignIteration1Enabled?: boolean }>`
  margin-top: 30px;
  width: ${(props) => (props.isDesignIteration1Enabled ? '75%' : '100%')};
`;

const CompleteFailureContent = ({
  onTryAgainClick,
  isDesignIteration1Enabled,
}: {
  onTryAgainClick?: () => void;
  isDesignIteration1Enabled?: boolean;
}) => (
  <>
    {isDesignIteration1Enabled ? (
      <StyledImgContainer src={OrderFailureWarningIcon as any} shouldCenterAlignImg={true} />
    ) : (
      <StyledWarningImg src={OrderFailureWarningIcon as any} />
    )}
    <StyledHeadingWrapper>
      <h1>We're unable to process your order</h1>
    </StyledHeadingWrapper>
    <StyledContentRow>
      Try again in a few minutes. If this keeps happening, contact <br />
      <Flex justifyContent="center">
        <a href={getPartnerServiceDeskUrlForGenericHelp()}>Partner Service Desk</a>.
      </Flex>
    </StyledContentRow>
    <Flex justifyContent={isDesignIteration1Enabled ? 'center' : 'stretch'}>
      <Button onClick={onTryAgainClick} shouldFitContainer={!isDesignIteration1Enabled} testId="try-again-btn">
        Try again
      </Button>
    </Flex>
  </>
);

const PartialFailure = ({
  heading,
  subHeading,
  isExistingSiteNewOrderPlaced,
  isDesignIteration1Enabled,
}: {
  heading: JSX.Element;
  subHeading: JSX.Element;
  isExistingSiteNewOrderPlaced?: boolean;
  isDesignIteration1Enabled?: boolean;
}) => (
  <Flex direction="column" alignItems="center" justifyContent="center">
    {isDesignIteration1Enabled ? (
      <Flex direction="column" justifyContent="center">
        {heading}
        {subHeading}
      </Flex>
    ) : (
      <>
        {heading} {subHeading}
      </>
    )}

    {isExistingSiteNewOrderPlaced && (
      <StyledSuccessContent>
        Successfully processed subscriptions will be added to applicable sites shortly.
      </StyledSuccessContent>
    )}
    <StyledSuccessContent>
      {isDesignIteration1Enabled
        ? `Successfully processed subscriptions will be added to applicable sites shortly.`
        : `You can view your order for successfully processed subscription changes on Partner Purchasing Center.`}
    </StyledSuccessContent>
  </Flex>
);

const PartialFailureWithNewSiteSuccess = ({
  heading,
  subHeading,
  hasExistingSiteSuccess = false,
  onGetStartedClick,
  isDesignIteration1Enabled,
}: {
  heading: JSX.Element;
  subHeading: JSX.Element;
  hasExistingSiteSuccess?: boolean;
  onGetStartedClick?: () => void;
  isDesignIteration1Enabled?: boolean;
}) => {
  const commonBodyContent = () => {
    const getStartedCTA = (
      <Button appearance="primary" onClick={onGetStartedClick} shouldFitContainer testId="site-provisioning-btn">
        Get started
      </Button>
    );
    return (
      <Flex direction="column">
        {hasExistingSiteSuccess ? (
          <StyledContentRow>
            {isDesignIteration1Enabled && (
              <StyledImgContainer src={PartialOrderSuccessIcon as any} shouldCenterAlignImg={false} />
            )}
            {isDesignIteration1Enabled && (
              <StyledHeadingWrapper>
                <h2>
                  <strong>Some items have been processed</strong>
                </h2>
              </StyledHeadingWrapper>
            )}

            <StyledContentCol>
              Successfully processed subscriptions:
              <ul>
                <li>
                  <strong>For existing sites</strong>, subscriptions will be added shortly.
                </li>
                <li>
                  <strong>For new sites</strong>, you can get started by setting up a cloud site. We’ve also sent a
                  notification to your customer with a link to set up a cloud site and get started.
                </li>
              </ul>
            </StyledContentCol>
          </StyledContentRow>
        ) : (
          <>
            {isDesignIteration1Enabled && (
              <StyledImgContainer src={PartialOrderSuccessIcon as any} shouldCenterAlignImg={false} />
            )}
            {isDesignIteration1Enabled && (
              <StyledHeadingWrapper>
                <h2>
                  <strong>Some items have been processed</strong>
                </h2>
              </StyledHeadingWrapper>
            )}
            <StyledContentRow>
              For successfully processed subscriptions, you can get started <br />
              by setting up a cloud site. We've also sent a notification to your <br />
              customer with a link to set up a cloud site and get started.
            </StyledContentRow>
          </>
        )}

        <StyledContentRow>
          {isDesignIteration1Enabled ? (
            <Box
              xcss={xcss({
                width: '30%',
              })}
            >
              {getStartedCTA}
            </Box>
          ) : (
            getStartedCTA
          )}
        </StyledContentRow>

        <StyledFooter isDesignIteration1Enabled={isDesignIteration1Enabled}>
          Whoever sets up the site will automatically get admin permissions as an org and a site admin. They can also
          give admin roles to others - see{' '}
          <a href={ADMIN_PERMISSION_URL} target="_blank">
            how to give users admin permissions.
          </a>
        </StyledFooter>
      </Flex>
    );
  };

  return (
    <Flex
      direction={isDesignIteration1Enabled ? 'row' : 'column'}
      alignItems="center"
      justifyContent="center"
      gap={isDesignIteration1Enabled ? 'space.800' : 'space.0'}
      xcss={xcss({
        paddingLeft: isDesignIteration1Enabled ? `${token('space.1000', '80px')}` : `${token('space.0', '0')}`,
        paddingRight: isDesignIteration1Enabled ? `${token('space.1000', '80px')}` : `${token('space.0', '0')}`,
      })}
    >
      <Box
        padding={isDesignIteration1Enabled ? 'space.200' : 'space.0'}
        xcss={xcss({ width: isDesignIteration1Enabled ? '50%' : '100%' })}
      >
        {isDesignIteration1Enabled ? (
          <Flex direction="column" justifyContent="center">
            {heading}
            {subHeading}
          </Flex>
        ) : (
          <>
            {heading}
            {subHeading}
          </>
        )}
      </Box>
      <StyledSuccessContent>
        {isDesignIteration1Enabled ? (
          <Box
            xcss={xcss({
              width: '80%',
              paddingTop: '64px',
              paddingLeft: '64px',
            })}
          >
            {commonBodyContent()}
          </Box>
        ) : (
          commonBodyContent()
        )}
      </StyledSuccessContent>
    </Flex>
  );
};

export const FailureScreenContainer = ({
  variant = FailureVariants.COMPLETE,
  onTryAgainClick,
  failureItemIds,
  onGetStartedClick,
  isExistingSiteNewOrderPlaced,
  isDesignIteration1Enabled,
}: {
  variant: FailureVariants;
  onTryAgainClick?: () => void;
  failureItemIds: string[];
  onGetStartedClick?: () => void;
  isExistingSiteNewOrderPlaced?: boolean;
  isDesignIteration1Enabled?: boolean;
}) => {
  const { isOpen: showFailedItemsModal, open: openFailedItemsModal, close: closeFailedItemsModal } = useOpenClose();

  const content = (() => {
    const failureHeading = `Some items couldn't be processed`;
    const partialFailureHeading = (
      <>
        {isDesignIteration1Enabled ? (
          <StyledImgContainer
            src={OrderFailureWarningIcon as any}
            shouldCenterAlignImg={variant === FailureVariants.PARTIAL}
          />
        ) : (
          <StyledWarningImg src={OrderFailureWarningIcon as any} />
        )}
        <StyledHeadingWrapper>
          {isDesignIteration1Enabled ? (
            <h2>
              <strong>{failureHeading}</strong>
            </h2>
          ) : (
            <h1>{failureHeading}</h1>
          )}
        </StyledHeadingWrapper>
      </>
    );

    const partialFailureSubHeading = (
      <>
        <StyledContentRow>You'll need to place an order for the following items again:</StyledContentRow>
        <StyledContentRow>
          <Flex justifyContent={isDesignIteration1Enabled ? 'center' : 'stretch'}>
            <Button
              onClick={() => openFailedItemsModal()}
              shouldFitContainer={!isDesignIteration1Enabled}
              testId="view-failed-items-btn"
            >
              View Items
            </Button>
          </Flex>
        </StyledContentRow>
      </>
    );

    switch (variant) {
      case FailureVariants.COMPLETE: {
        return (
          <CompleteFailureContent
            onTryAgainClick={onTryAgainClick}
            isDesignIteration1Enabled={isDesignIteration1Enabled}
          />
        );
      }
      case FailureVariants.PARTIAL: {
        return (
          <PartialFailure
            heading={partialFailureHeading}
            subHeading={partialFailureSubHeading}
            isExistingSiteNewOrderPlaced={isExistingSiteNewOrderPlaced}
            isDesignIteration1Enabled={isDesignIteration1Enabled}
          />
        );
      }
      case FailureVariants.PARTIAL_WITH_ONLY_NEW_SITE_SUCCESS: {
        return (
          <PartialFailureWithNewSiteSuccess
            onGetStartedClick={onGetStartedClick}
            heading={partialFailureHeading}
            subHeading={partialFailureSubHeading}
            isDesignIteration1Enabled={isDesignIteration1Enabled}
          />
        );
      }
      case FailureVariants.PARTIAL_WITH_SOME_EXISTING_AND_NEW_SITE_SUCCESS: {
        return (
          <PartialFailureWithNewSiteSuccess
            onGetStartedClick={onGetStartedClick}
            heading={partialFailureHeading}
            subHeading={partialFailureSubHeading}
            hasExistingSiteSuccess
            isDesignIteration1Enabled={isDesignIteration1Enabled}
          />
        );
      }
    }
  })();
  return (
    <StyledFailureScreenWrapper>
      {!isDesignIteration1Enabled && (
        <div className="order-confirmation-container-atlassian-logo">
          <AtlassianLogo appearance="brand" size={'medium'} />
        </div>
      )}
      <FailureItemModal isOpen={showFailedItemsModal} onClose={closeFailedItemsModal} failureItemIds={failureItemIds} />
      <StyledContentWrapper isDesignIteration1Enabled={isDesignIteration1Enabled}>{content}</StyledContentWrapper>
    </StyledFailureScreenWrapper>
  );
};
